import type { RelationshipGraph as RG } from "../api";

// ─── Layout rules (keep these in mind when editing) ────────────────────────
// 1. Character names sit *radially outward* from each node, so adjacent
//    nodes can't bump into each other's labels.
// 2. text-anchor depends on the node's angle (left side = end, right side =
//    start, top/bottom = middle). This keeps text reading outward.
// 3. Edge labels are drawn at the curve midpoint with a pill-shaped backing
//    rect sized from a calibrated text width (NOT raw char count).
// 4. Only PRIMARY and SECONDARY edges get labels — background edges are
//    drawn as thin lines without text, which keeps dense graphs legible.
// 5. SVG viewBox padding accounts for the longest expected label so nothing
//    spills outside the visible area on the edges.

const CHAR_WIDTHS: Record<string, number> = {};
const baseCharWidth = 6.2; // Inter at ~11.5px averages this width per char
function textWidth(s: string, perChar = baseCharWidth): number {
  let w = 0;
  for (const ch of s) {
    w += CHAR_WIDTHS[ch] ?? (ch === " " ? perChar * 0.55 : perChar);
  }
  return Math.max(w, perChar * 3);
}

interface Props {
  graph: RG;
}

const intensityStyle = {
  primary: { stroke: "#C77B85", width: 1.7, opacity: 0.95, showLabel: true },
  secondary: { stroke: "#C9A961", width: 1.2, opacity: 0.75, showLabel: true },
  background: { stroke: "#A695A8", width: 0.8, opacity: 0.40, showLabel: false },
};

export function RelationshipGraph({ graph }: Props) {
  const people =
    graph.people && graph.people.length > 0
      ? graph.people
      : Array.from(new Set(graph.edges.flatMap((e) => [e.from, e.to]))).map(
          (name) => ({ name, role: "" })
        );

  const n = people.length;
  if (n === 0) return null;

  // Generous viewBox so radial labels don't clip on the sides.
  const width = 820;
  const height = 620;
  const cx = width / 2;
  const cy = height / 2;
  // Pull the ring closer to the center for small N (lots of label room),
  // expand it for large N (more space between nodes).
  const r = Math.min(width, height) * (n <= 6 ? 0.30 : n <= 10 ? 0.34 : 0.38);

  // Compute each node's anchor info + label box ahead of edge drawing so
  // edges can dodge them later if needed.
  const nodes = people.map((p, i) => {
    const angle = -Math.PI / 2 + (i / n) * 2 * Math.PI;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);

    // Label sits radially outward from the node.
    const labelDist = 22;
    const lx = cx + (r + labelDist) * Math.cos(angle);
    const ly = cy + (r + labelDist) * Math.sin(angle);

    // text-anchor based on angle quadrant — read outward, never overlap node.
    const cos = Math.cos(angle);
    const anchor: "start" | "middle" | "end" =
      cos > 0.34 ? "start" : cos < -0.34 ? "end" : "middle";
    const dy = Math.sin(angle) > 0 ? 12 : -2;

    return {
      name: p.name,
      role: p.role,
      x,
      y,
      angle,
      lx,
      ly,
      anchor,
      dyName: dy,
      dyRole: dy + 14,
    };
  });

  const byName: Record<string, typeof nodes[number]> = {};
  nodes.forEach((nd) => (byName[nd.name] = nd));

  // Group edges by node-pair so parallel edges can fan out instead of stacking.
  const pairCount: Record<string, number> = {};
  const pairIdx: number[] = [];
  graph.edges.forEach((e) => {
    const k = [e.from, e.to].sort().join("§");
    const idx = pairCount[k] ?? 0;
    pairIdx.push(idx);
    pairCount[k] = idx + 1;
  });

  // Build the edge paths + label positions in one pass.
  const drawnEdges = graph.edges.map((e, i) => {
    const a = byName[e.from];
    const b = byName[e.to];
    if (!a || !b) return null;

    const k = [e.from, e.to].sort().join("§");
    const total = pairCount[k] ?? 1;
    const idx = pairIdx[i] ?? 0;

    // Bend the curve toward the center, scaled by parallel index so duplicates
    // splay apart.
    const midX = (a.x + b.x) / 2;
    const midY = (a.y + b.y) / 2;
    const toCenterX = cx - midX;
    const toCenterY = cy - midY;
    const baseBend = 0.28;
    const parallelOffset = total > 1 ? ((idx - (total - 1) / 2) * 0.18) : 0;
    const bend = baseBend + parallelOffset;

    const ctrlX = midX + toCenterX * bend;
    const ctrlY = midY + toCenterY * bend;

    // Curve midpoint (quadratic bezier) for label placement.
    const t = 0.5;
    const labelX = (1 - t) ** 2 * a.x + 2 * (1 - t) * t * ctrlX + t ** 2 * b.x;
    const labelY = (1 - t) ** 2 * a.y + 2 * (1 - t) * t * ctrlY + t ** 2 * b.y;

    return {
      edge: e,
      a,
      b,
      path: `M ${a.x.toFixed(1)} ${a.y.toFixed(1)} Q ${ctrlX.toFixed(1)} ${ctrlY.toFixed(1)} ${b.x.toFixed(1)} ${b.y.toFixed(1)}`,
      labelX,
      labelY,
    };
  });

  // Greedy label collision resolution: nudge each edge label off any node
  // label and any previously placed edge label until no overlap remains.
  // We work in "boxes" — { x, y, w, h } where x,y is the center.
  type Box = { x: number; y: number; w: number; h: number };
  const labelHeight = 18;
  const nodeBoxes: Box[] = nodes.map((nd) => {
    const w = Math.max(textWidth(nd.name, 7.4), nd.role ? textWidth(nd.role, 5.4) : 0) + 8;
    const cxBox = nd.lx + (nd.anchor === "start" ? w / 2 - 4 : nd.anchor === "end" ? -w / 2 + 4 : 0);
    const cyBox = nd.ly + (nd.role ? 4 : 0);
    return { x: cxBox, y: cyBox, w, h: nd.role ? 30 : labelHeight };
  });

  const edgeBoxes: Box[] = [];
  const placedEdgeLabels = drawnEdges.map((d) => {
    if (!d) return null;
    const style = intensityStyle[d.edge.intensity] ?? intensityStyle.secondary;
    if (!style.showLabel || !d.edge.label) return null;

    const w = textWidth(d.edge.label, 5.6) + 14;
    let bx = d.labelX;
    let by = d.labelY;
    const allObstacles = [...nodeBoxes, ...edgeBoxes];

    for (let attempt = 0; attempt < 6; attempt++) {
      const myBox: Box = { x: bx, y: by, w, h: labelHeight };
      const collider = allObstacles.find((o) => boxesOverlap(myBox, o, 3));
      if (!collider) break;
      // Push away from collider along the vector between centers.
      const dx = bx - collider.x;
      const dy = by - collider.y;
      const dist = Math.hypot(dx, dy) || 1;
      const overlapX = (myBox.w + collider.w) / 2 - Math.abs(dx);
      const overlapY = (myBox.h + collider.h) / 2 - Math.abs(dy);
      const push = Math.max(overlapX, overlapY, 4) + 2;
      bx += (dx / dist) * push;
      by += (dy / dist) * push;
    }

    const box: Box = { x: bx, y: by, w, h: labelHeight };
    edgeBoxes.push(box);
    return { x: bx, y: by, w };
  });

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full max-w-4xl mx-auto h-auto"
        role="img"
        aria-label="Character relationship graph"
        style={{ minWidth: 320 }}
      >
        <defs>
          <radialGradient id="rg-bg" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#FBF7F4" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#FBF7F4" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Soft halo behind everything */}
        <circle cx={cx} cy={cy} r={r * 1.1} fill="url(#rg-bg)" />

        {/* Edges first so nodes + labels render on top */}
        <g>
          {drawnEdges.map((d, i) => {
            if (!d) return null;
            const style = intensityStyle[d.edge.intensity] ?? intensityStyle.secondary;
            return (
              <path
                key={`edge-${i}`}
                d={d.path}
                fill="none"
                stroke={style.stroke}
                strokeWidth={style.width}
                strokeOpacity={style.opacity}
                strokeLinecap="round"
              />
            );
          })}
        </g>

        {/* Edge labels with pill backgrounds */}
        <g>
          {placedEdgeLabels.map((p, i) => {
            const d = drawnEdges[i];
            if (!d || !p) return null;
            const style = intensityStyle[d.edge.intensity] ?? intensityStyle.secondary;
            return (
              <g key={`elabel-${i}`}>
                <rect
                  x={p.x - p.w / 2}
                  y={p.y - 9}
                  width={p.w}
                  height={18}
                  rx={9}
                  fill="#FBF7F4"
                  stroke={style.stroke}
                  strokeOpacity={0.5}
                />
                <text
                  x={p.x}
                  y={p.y + 4}
                  textAnchor="middle"
                  fontSize="10.5"
                  fontFamily="Inter, sans-serif"
                  fill="#5d4660"
                  fontStyle="italic"
                >
                  {d.edge.label}
                </text>
              </g>
            );
          })}
        </g>

        {/* Nodes */}
        <g>
          {nodes.map((nd) => (
            <g key={nd.name}>
              <circle
                cx={nd.x}
                cy={nd.y}
                r={9}
                fill="#C77B85"
                stroke="#FBF7F4"
                strokeWidth={3}
              />
              <text
                x={nd.lx}
                y={nd.ly + nd.dyName}
                textAnchor={nd.anchor}
                fontSize="13"
                fontFamily="'Playfair Display', serif"
                fill="#3D2B3D"
              >
                {nd.name}
              </text>
              {nd.role ? (
                <text
                  x={nd.lx}
                  y={nd.ly + nd.dyRole}
                  textAnchor={nd.anchor}
                  fontSize="10"
                  fontFamily="Inter, sans-serif"
                  fill="#7a5e7d"
                  fontStyle="italic"
                >
                  {nd.role}
                </text>
              ) : null}
            </g>
          ))}
        </g>
      </svg>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-5 mt-5 text-xs text-plum-soft">
        <Legend swatch={intensityStyle.primary.stroke} label="Primary" />
        <Legend swatch={intensityStyle.secondary.stroke} label="Secondary" />
        <Legend swatch={intensityStyle.background.stroke} label="Background" />
      </div>
    </div>
  );
}

function boxesOverlap(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }, padding = 0): boolean {
  return (
    Math.abs(a.x - b.x) < (a.w + b.w) / 2 + padding &&
    Math.abs(a.y - b.y) < (a.h + b.h) / 2 + padding
  );
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-6 h-0.5 rounded-full" style={{ background: swatch }} />
      <span className="uppercase tracking-[0.14em] text-[0.65rem]">{label}</span>
    </div>
  );
}
